import { hasAnyScheduleTargets } from './scheduleTargetUtils';

/** Dispatched when the user toggles practice vs patient schedule targets or saves targets. */
export const PATIENT_SCHEDULE_TARGET_PREF_EVENT = 'rpm:patient-schedule-target-pref';

const storageKey = (practiceId, patientId) =>
  `rpm_use_patient_schedule_targets:v1:${String(practiceId)}:${String(patientId)}`;

const customTargetsKey = (practiceId, patientId) =>
  `rpm_patient_has_custom_targets:v1:${String(practiceId)}:${String(patientId)}`;

/** Cache whether patient has custom schedule targets */
export function setPatientHasCustomTargets(practiceId, patientId, hasCustom) {
  if (practiceId == null || patientId == null) return;
  try {
    const val = hasCustom ? '1' : '0';
    localStorage.setItem(customTargetsKey(practiceId, patientId), val);
    sessionStorage.setItem(customTargetsKey(practiceId, patientId), val);
  } catch {
    /* ignore */
  }
}

export function getPatientHasCustomTargets(practiceId, patientId) {
  if (practiceId == null || patientId == null) return null;
  try {
    const val =
      localStorage.getItem(customTargetsKey(practiceId, patientId)) ??
      sessionStorage.getItem(customTargetsKey(practiceId, patientId));
    if (val === '1') return true;
    if (val === '0') return false;
    return null;
  } catch {
    return null;
  }
}

/**
 * Get whether patient schedule targets should be used.
 * 
 * Precedence rules:
 * 1. Explicit user choice in storage ('1' = patient mode, '0' = practice mode).
 * 2. If no explicit user choice:
 *    - If patientData is provided: true if patient has any custom targets, false otherwise.
 *    - If cached custom target flag exists: returns cached custom targets flag.
 *    - Defaults to false (practice mode) if no custom targets exist.
 */
export function getUsePatientScheduleTargetsPreference(practiceId, patientId, patientData = null) {
  if (practiceId == null || patientId == null) return false;

  let stored = null;
  try {
    stored =
      localStorage.getItem(storageKey(practiceId, patientId)) ??
      sessionStorage.getItem(storageKey(practiceId, patientId));
  } catch {
    /* ignore */
  }

  // Explicit user override
  if (stored === '1' || stored === 'patient' || stored === 'true') {
    return true;
  }
  if (stored === '0' || stored === 'practice' || stored === 'false') {
    return false;
  }

  // Auto-detect based on patientData if available
  if (patientData && typeof patientData === 'object') {
    const hasCustom = hasAnyScheduleTargets(patientData);
    setPatientHasCustomTargets(practiceId, patientId, hasCustom);
    return hasCustom;
  }

  // Check cached flag
  const cachedCustom = getPatientHasCustomTargets(practiceId, patientId);
  if (cachedCustom !== null) {
    return cachedCustom;
  }

  return false;
}

/**
 * Whether patient-level schedule/target rows should apply for categorization at measurement time `ms`.
 * If patient mode is active (or patient has targets), patient targets apply to all readings displayed.
 */
export function getUsePatientScheduleTargetsAtMeasurementMs(practiceId, patientId, ms, patientData = null) {
  if (practiceId == null || patientId == null) return false;
  return getUsePatientScheduleTargetsPreference(practiceId, patientId, patientData);
}

export function setUsePatientScheduleTargetsPreference(practiceId, patientId, enabled) {
  if (practiceId == null || patientId == null) return;
  const val = enabled ? '1' : '0';
  try {
    localStorage.setItem(storageKey(practiceId, patientId), val);
    sessionStorage.setItem(storageKey(practiceId, patientId), val);
  } catch {
    /* ignore quota / private mode */
  }
  try {
    window.dispatchEvent(
      new CustomEvent(PATIENT_SCHEDULE_TARGET_PREF_EVENT, {
        detail: { practiceId: String(practiceId), patientId: String(patientId), enabled: !!enabled }
      })
    );
  } catch {
    /* noop */
  }
}
